import React, { useEffect, useRef, useState } from 'react';
import { Heart, Star, DollarSign, Search, Grid, List, Eye, CheckCircle } from 'lucide-react';
import { sampleVendors, vendorCategories, vendorLocations } from '../data/sampleVendors';
import type { Vendor, VendorFilters, VendorCategory } from '../types/vendors';
import { getPublicVendors } from '../lib/admin';
import { transformAdminVendorToVendor } from '../utils/dataTransform';

export default function VendorDirectory() {
  const sparklesRef = useRef<HTMLDivElement>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [savedVendors, setSavedVendors] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<VendorFilters>({
    searchTerm: '',
    category: 'all',
    maxPrice: 1000000,
    location: 'All Locations',
    rating: 0
  });

  useEffect(() => {
    loadVendors();
  }, []);

  async function loadVendors() {
    try {
      setLoading(true);
      const dbVendors = await getPublicVendors();
      const transformedVendors = dbVendors.map(transformAdminVendorToVendor);
      setVendors([...transformedVendors, ...sampleVendors]);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setVendors(sampleVendors);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);
    return () => clearInterval(interval);
  }, []);

  const toggleSaveVendor = (vendorId: string) => {
    setSavedVendors(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                         vendor.location.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                         vendor.specialties.some(s => s.toLowerCase().includes(filters.searchTerm.toLowerCase()));
    const matchesCategory = filters.category === 'all' || vendor.category === filters.category;
    const matchesPrice = vendor.priceRange.max <= filters.maxPrice;
    const matchesLocation = filters.location === 'All Locations' || vendor.city === filters.location;
    const matchesRating = vendor.rating >= filters.rating;
    
    return matchesSearch && matchesCategory && matchesPrice && matchesLocation && matchesRating;
  });

  const formatPriceRange = (vendor: Vendor) => {
    return `LKR ${vendor.priceRange.min.toLocaleString()} - ${vendor.priceRange.max.toLocaleString()} ${vendor.priceRange.unit}`;
  };

  const getCategoryStats = () => {
    const stats: Record<string, number> = {};
    vendors.forEach(vendor => {
      stats[vendor.category] = (stats[vendor.category] || 0) + 1;
    });
    return stats;
  };

  const categoryStats = getCategoryStats();
  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-white">This Option is Coming Soon</h1>
        <h1 className="text-4xl font-bold text-white">Find Your Wedding Vendors</h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
          Connect with trusted professionals to bring your wedding vision to life
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-white/40" />
            <input
              type="text"
              placeholder="Search vendors by name, location, or specialty..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              className="pl-10 luxury-input"
            />
          </div>
          
          <div>
            <select
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              className="luxury-input"
            >
              {vendorLocations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <div>
            <input
              type="number"
              placeholder="Max budget (LKR)"
              value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: Number(e.target.value) || 1000000 })}
              className="luxury-input"
              min="0"
            />
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-800/50 text-white rounded-lg hover:bg-gray-700/50 transition-colors"
            >
              {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
              <span>{viewMode === 'grid' ? 'List' : 'Grid'}</span>
            </button>
          </div>
        </div>

        {/* Rating Filter */}
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-white/80">Minimum Rating:</span>
          <div className="flex items-center space-x-2">
            {[0, 4.0, 4.5, 4.8].map(rating => (
              <button
                key={rating}
                onClick={() => setFilters({ ...filters, rating })}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  filters.rating === rating
                    ? 'bg-primary text-white'
                    : 'bg-gray-800/50 text-white hover:bg-gray-700/50'
                }`}
              >
                {rating === 0 ? 'Any' : `${rating}+ ⭐`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-3 justify-center">
        {vendorCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => setFilters({ ...filters, category: category.id as VendorCategory | 'all' })}
            className={`flex items-center space-x-2 px-4 py-2 rounded-full border-2 transition-all duration-200 ${
              filters.category === category.id
                ? 'bg-primary text-white border-primary shadow-lg'
                : `${category.color} border-transparent hover:shadow-md`
            }`}
          >
            <category.icon className="w-4 h-4" />
            <span className="font-medium">
              {category.name}
              {category.id !== 'all' && categoryStats[category.id] && (
                <span className="ml-1 text-xs opacity-75">({categoryStats[category.id]})</span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-white">
          Showing {filteredVendors.length} of {vendors.length} vendors
        </p>
        <p className="text-gray-300">
          {savedVendors.length} vendors saved
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <p className="text-white mt-4">Loading vendors...</p>
        </div>
      )}

      {/* Vendors Grid */}
      {!loading && (
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
        {filteredVendors.map((vendor) => (
          <div
            key={vendor.id}
            className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg overflow-hidden hover:scale-105 transition-all duration-300"
          >
            <div className="relative">
              <img
                src={vendor.image}
                alt={vendor.name}
                className={`w-full object-cover ${viewMode === 'grid' ? 'h-48' : 'h-32'}`}
              />
              <button
                onClick={() => toggleSaveVendor(vendor.id)}
                className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
                  savedVendors.includes(vendor.id)
                    ? 'bg-red-500 text-white'
                    : 'bg-white/80 text-gray-600 hover:bg-white'
                }`}
              >
                <Heart className={`w-5 h-5 ${savedVendors.includes(vendor.id) ? 'fill-current' : ''}`} />
              </button>
              {vendor.verified && (
                <div className="absolute top-4 left-4">
                  <div className="flex items-center space-x-1 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                    <CheckCircle className="w-3 h-3" />
                    <span>Verified</span>
                  </div>
                </div>
              )}
            </div>

            <div className={`p-6 space-y-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
              <div>
                <h3 className="text-xl font-bold text-white">{vendor.name}</h3>
                <div className="flex items-center space-x-2 text-white/70">
                  <span>{vendor.location}</span>
                </div>
              </div>

              {vendor.description && (
                <p className="text-white/70 text-sm line-clamp-2">
                  {vendor.description}
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                {vendor.specialties.slice(0, 3).map((specialty, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {specialty}
                  </span>
                ))}
                {vendor.specialties.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{vendor.specialties.length - 3} more
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="font-medium text-white">{vendor.rating}</span>
                  <span className="text-white/70">({vendor.reviews} reviews)</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-white/20">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-white text-sm">{formatPriceRange(vendor)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="flex items-center space-x-1 px-3 py-1 text-primary hover:text-primary-600 text-sm">
                    <Eye className="w-4 h-4" />
                    <span>Details</span>
                  </button>
                  <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors">
                    Contact
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {!loading && filteredVendors.length === 0 && (
        <div className="text-center py-12 bg-gray-800/50 backdrop-blur-sm rounded-xl">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No vendors found</h3>
          <p className="text-gray-300 mb-6">Try adjusting your search criteria or filters</p>
          <button
            onClick={() => setFilters({
              searchTerm: '',
              category: 'all',
              maxPrice: 1000000,
              location: 'All Locations',
              rating: 0
            })}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* AI Recommendations */}
      <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
        <h3 className="text-xl font-bold text-white mb-4">🤖 AI Vendor Recommendations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-900/50 rounded-lg p-4">
            <p className="text-white font-medium mb-2">📸 Booking Timeline</p>
            <p className="text-white/70 text-sm">
              Book your photographer 8-12 months in advance. Popular photographers get booked quickly!
            </p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-4">
            <p className="text-white font-medium mb-2">💰 Budget Optimization</p>
            <p className="text-white/70 text-sm">
              Consider bundling services with vendors who offer multiple specialties to save 15-20% on total costs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}